// Create Tenant form — field/tab model shared by the tabbed form.
//
// The form is grouped into logical TABS. Each tab key matches the backend
// service's TenantFormTab union (services.tenantProvisioning.ts) so a promote
// 422 fieldError.tab lands on the right tab. Draft-save is LENIENT (any state
// persists); these validators run only on strict submit / for tab badges.

const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Phone: digits only, 8–15 long (optionally a leading +). Same rule for the
// billing phone and admin mobile, so lengths are validated consistently.
const PHONE_REGEX = /^\+?\d{8,15}$/;

// Platform defaults (must match :root in src/index.css).
export const DEFAULT_COLORS = {
  primaryColor: '#0f538d',
  secondaryColor: '#2fab73',
  accentColor: '#ffa726',
};

// Tab definitions — order is the display order. `key` is the stable id used
// for TabList value, error badges, lastTabKey persistence, and backend
// fieldError.tab mapping.
export const TENANT_FORM_TABS = [
  { key: 'identity', label: 'Identity' },
  { key: 'branding', label: 'Branding' },
  { key: 'billing', label: 'Billing / Invoice' },
  { key: 'communications', label: 'Communications' },
  { key: 'roles', label: 'Roles & Admin' },
  { key: 'review', label: 'Review & Submit' },
];

// Which flat form field lives on which tab. Also maps backend dotted
// fieldError paths (e.g. "admin.email", "invoice.gstin") to a form field +
// tab so a promote 422 can badge the tab and focus the field.
export const FIELD_TAB = {
  name: 'identity',
  cities: 'identity',
  subdomain: 'identity',
  primaryColor: 'branding',
  secondaryColor: 'branding',
  accentColor: 'branding',
  logo: 'branding',
  invLegalName: 'billing',
  invGstin: 'billing',
  invPan: 'billing',
  invRegisteredAddress: 'billing',
  invEmail: 'billing',
  invPhone: 'billing',
  invWebsite: 'billing',
  invJurisdiction: 'billing',
  smtpHost: 'communications',
  smtpPort: 'communications',
  smtpUsername: 'communications',
  smtpPassword: 'communications',
  smtpFromEmail: 'communications',
  smtpFromName: 'communications',
  roleManagementEnabled: 'roles',
};
// Admins are a dynamic array; their error keys are `admins.<i>.<field>` and
// always belong to the roles tab. tabForField() below understands both the
// static FIELD_TAB map and these indexed admin keys.

// The tab an error key belongs to (static field or an admins.<i>.<field> key).
export const tabForField = (field) => {
  if (typeof field === 'string' && field.startsWith('admins.')) return 'roles';
  return FIELD_TAB[field];
};

// Map a backend dotted fieldError path to the local form-error key. Admin
// paths (admins.<i>.<field>) are already in the form's own key shape, so they
// pass through unchanged. The legacy single admin.<field> maps to admins.0.
export const backendFieldToFormField = (path) => {
  if (typeof path === 'string' && path.startsWith('admins.')) return path;
  const map = {
    name: 'name',
    subdomain: 'subdomain',
    cities: 'cities',
    'branding.logo': 'logo',
    'invoice.legalName': 'invLegalName',
    'invoice.gstin': 'invGstin',
    'invoice.pan': 'invPan',
    'invoice.registeredAddress': 'invRegisteredAddress',
    'invoice.email': 'invEmail',
    'invoice.phone': 'invPhone',
    'invoice.website': 'invWebsite',
    'invoice.jurisdiction': 'invJurisdiction',
    'smtp.host': 'smtpHost',
    'smtp.port': 'smtpPort',
    'smtp.username': 'smtpUsername',
    'smtp.password': 'smtpPassword',
    'smtp.fromEmail': 'smtpFromEmail',
    'admin.firstName': 'admins.0.firstName',
    'admin.lastName': 'admins.0.lastName',
    'admin.mobile': 'admins.0.mobile',
    'admin.countryCode': 'admins.0.countryCode',
    'admin.email': 'admins.0.email',
  };
  return map[path] || null;
};

// A blank admin block for the form.
export const emptyAdmin = () => ({
  firstName: '',
  lastName: '',
  mobile: '',
  countryCode: '+91',
  email: '',
});

export const initialFormState = {
  name: '',
  subdomain: '',
  primaryColor: DEFAULT_COLORS.primaryColor,
  secondaryColor: DEFAULT_COLORS.secondaryColor,
  accentColor: DEFAULT_COLORS.accentColor,
  invLegalName: '',
  invGstin: '',
  invPan: '',
  invRegisteredAddress: '',
  invEmail: '',
  invPhone: '',
  invWebsite: '',
  invJurisdiction: '',
  smtpHost: '',
  smtpPort: '',
  smtpUsername: '',
  smtpPassword: '',
  smtpFromEmail: '',
  smtpFromName: '',
  // One or more equal admins (all role 1). Starts with a single blank block;
  // "Add another admin" appends more.
  admins: [emptyAdmin()],
  roleManagementEnabled: 'no',
};

// Strict client-side validation (mirrors the backend). Returns a map of
// { formField: message }. Empty map = valid. Note: SMTP password can't be
// re-validated for presence once only stored server-side (drafts don't return
// it), so SMTP is validated only when the user has (re)entered host locally.
export const validateForm = (form, hasLogo) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Name is required';

  if (
    form.subdomain.trim() &&
    !/^[a-z0-9](?:[a-z0-9-]{1,28}[a-z0-9])?$/.test(form.subdomain.trim().toLowerCase())
  ) {
    errors.subdomain = '2-30 chars: lowercase letters, digits, hyphens';
  }

  ['primaryColor', 'secondaryColor', 'accentColor'].forEach((field) => {
    if (form[field].trim() && !HEX_COLOR_REGEX.test(form[field].trim())) {
      errors[field] = 'Must be a hex color like #1A73E8';
    }
  });

  if (!hasLogo) errors.logo = 'Logo is required — printed on invoices';

  if (!form.invLegalName.trim()) errors.invLegalName = 'Required — printed on invoices';
  if (!form.invGstin.trim()) {
    errors.invGstin = 'Required — printed on invoices';
  } else if (!GSTIN_REGEX.test(form.invGstin.trim().toUpperCase())) {
    errors.invGstin = 'Enter a valid 15-character GSTIN';
  }
  if (!form.invPan.trim()) {
    errors.invPan = 'Required — printed on invoices';
  } else if (!PAN_REGEX.test(form.invPan.trim().toUpperCase())) {
    errors.invPan = 'Enter a valid 10-character PAN';
  } else if (
    GSTIN_REGEX.test(form.invGstin.trim().toUpperCase()) &&
    form.invGstin.trim().toUpperCase().substring(2, 12) !== form.invPan.trim().toUpperCase()
  ) {
    errors.invPan = 'PAN must match the PAN embedded in the GSTIN (chars 3-12)';
  }
  if (!form.invRegisteredAddress.trim())
    errors.invRegisteredAddress = 'Required — printed on invoices';
  if (!form.invEmail.trim()) {
    errors.invEmail = 'Required — printed on invoices';
  } else if (!EMAIL_REGEX.test(form.invEmail.trim())) {
    errors.invEmail = 'Enter a valid email';
  }
  if (!form.invPhone.trim()) {
    errors.invPhone = 'Required — printed on invoices';
  } else if (!PHONE_REGEX.test(form.invPhone.trim())) {
    errors.invPhone = 'Enter a valid phone number (8–15 digits)';
  }
  // Website is OPTIONAL; validate the format only when one is entered.
  if (
    form.invWebsite.trim() &&
    !/^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/.test(form.invWebsite.trim())
  ) {
    errors.invWebsite = 'Enter a valid website like www.example.com';
  }
  if (!form.invJurisdiction.trim())
    errors.invJurisdiction = 'Required — the disputes-jurisdiction city on invoices';

  // SMTP: optional as a whole, all-or-nothing.
  const smtpCore = ['smtpHost', 'smtpPort', 'smtpUsername', 'smtpPassword', 'smtpFromEmail'];
  const smtpTouched = [...smtpCore, 'smtpFromName'].some((f) => form[f].trim());
  if (smtpTouched) {
    smtpCore.forEach((f) => {
      if (!form[f].trim()) errors[f] = 'Required when configuring SMTP';
    });
    if (form.smtpPort.trim() && !/^\d+$/.test(form.smtpPort.trim())) {
      errors.smtpPort = 'Port must be a number';
    }
    if (form.smtpFromEmail.trim() && !EMAIL_REGEX.test(form.smtpFromEmail.trim())) {
      errors.smtpFromEmail = 'Enter a valid email';
    }
  }

  // Admins — at least one, each with required fields. Error keys are
  // admins.<i>.<field> (matching the backend fieldError paths).
  const admins = Array.isArray(form.admins) ? form.admins : [];
  if (!admins.length) {
    errors['admins.0.firstName'] = 'At least one admin is required';
  }
  admins.forEach((admin, i) => {
    if (!String(admin.firstName || '').trim()) {
      errors[`admins.${i}.firstName`] = 'First name is required';
    }
    if (!String(admin.mobile || '').trim()) {
      errors[`admins.${i}.mobile`] = 'Mobile is required';
    } else if (!PHONE_REGEX.test(String(admin.mobile).trim())) {
      errors[`admins.${i}.mobile`] = 'Enter a valid mobile number (8–15 digits)';
    }
    if (!String(admin.email || '').trim()) {
      errors[`admins.${i}.email`] = 'Email is required — it is the login credential';
    } else if (!EMAIL_REGEX.test(String(admin.email).trim())) {
      errors[`admins.${i}.email`] = 'Enter a valid email';
    }
  });

  return errors;
};

// Count of errors per tab, for the tab badges.
export const errorsByTab = (errors) => {
  const counts = {};
  Object.keys(errors).forEach((field) => {
    const tab = tabForField(field);
    if (tab) counts[tab] = (counts[tab] || 0) + 1;
  });
  return counts;
};

// Serialize the flat form + selected cities into the draft `payload` shape the
// backend expects (name, subdomain, cities[], admin{}, invoice{}, smtp{},
// config{}). The staged logo lives on the draft's `branding` (server-side),
// not here — this is only the text state.
export const formToDraftPayload = (form, selectedCities) => {
  const payload = {
    name: form.name.trim(),
    subdomain: form.subdomain.trim() || undefined,
    roleManagementEnabled: form.roleManagementEnabled === 'yes',
    config: { roleManagementEnabled: form.roleManagementEnabled === 'yes' },
    branding: {
      primaryColor: form.primaryColor.trim() || undefined,
      secondaryColor: form.secondaryColor.trim() || undefined,
      accentColor: form.accentColor.trim() || undefined,
      name: form.name.trim() || undefined,
    },
    invoice: {
      legalName: form.invLegalName.trim(),
      gstin: form.invGstin.trim().toUpperCase(),
      pan: form.invPan.trim().toUpperCase(),
      registeredAddress: form.invRegisteredAddress.trim(),
      email: form.invEmail.trim(),
      phone: form.invPhone.trim(),
      website: form.invWebsite.trim(),
      jurisdiction: form.invJurisdiction.trim(),
    },
    admins: (form.admins || []).map((a) => ({
      firstName: (a.firstName || '').trim(),
      lastName: (a.lastName || '').trim() || undefined,
      mobile: (a.mobile || '').trim(),
      countryCode: (a.countryCode || '').trim() || '+91',
      email: (a.email || '').trim(),
    })),
    cities: selectedCities.map((c) => c.id),
  };
  // SMTP only when the host is set (all-or-nothing).
  if (form.smtpHost.trim()) {
    payload.smtp = {
      host: form.smtpHost.trim(),
      port: form.smtpPort.trim() ? Number(form.smtpPort.trim()) : undefined,
      username: form.smtpUsername.trim(),
      password: form.smtpPassword,
      fromEmail: form.smtpFromEmail.trim(),
      fromName: form.smtpFromName.trim() || undefined,
    };
  }
  return payload;
};

// Hydrate the flat form state from a loaded draft's payload (inverse of
// formToDraftPayload). Cities are resolved against the canon by the caller.
export const draftPayloadToForm = (payload) => {
  const p = payload || {};
  const branding = p.branding || {};
  const invoice = p.invoice || {};
  // Accept the new `admins` array or a legacy single `admin`; always end up
  // with at least one block.
  const rawAdmins =
    Array.isArray(p.admins) && p.admins.length ? p.admins : p.admin ? [p.admin] : [];
  const smtp = p.smtp || {};
  return {
    ...initialFormState,
    name: p.name || '',
    subdomain: p.subdomain || '',
    primaryColor: branding.primaryColor || DEFAULT_COLORS.primaryColor,
    secondaryColor: branding.secondaryColor || DEFAULT_COLORS.secondaryColor,
    accentColor: branding.accentColor || DEFAULT_COLORS.accentColor,
    invLegalName: invoice.legalName || '',
    invGstin: invoice.gstin || '',
    invPan: invoice.pan || '',
    invRegisteredAddress: invoice.registeredAddress || '',
    invEmail: invoice.email || '',
    invPhone: invoice.phone || '',
    invWebsite: invoice.website || '',
    invJurisdiction: invoice.jurisdiction || '',
    smtpHost: smtp.host || '',
    smtpPort: smtp.port != null ? String(smtp.port) : '',
    smtpUsername: smtp.username || '',
    // The password is stored encrypted server-side and never returned; leave
    // blank on resume. Re-entering it re-sends; leaving it blank keeps the
    // stored one (the backend re-validates completeness at promote).
    smtpPassword: '',
    smtpFromEmail: smtp.fromEmail || '',
    smtpFromName: smtp.fromName || '',
    admins: rawAdmins.length
      ? rawAdmins.map((a) => ({
          firstName: a.firstName || '',
          lastName: a.lastName || '',
          mobile: a.mobile || '',
          countryCode: a.countryCode || '+91',
          email: a.email || '',
        }))
      : [emptyAdmin()],
    roleManagementEnabled:
      p.roleManagementEnabled || p.config?.roleManagementEnabled ? 'yes' : 'no',
  };
};
